import { cn } from '@/lib/utils';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useNavigationType, useSearchParams } from 'react-router-dom';
import { useListContext } from 'ra-core';
import { List } from '@/components/admin/list';
import { ListPagination } from '@/components/admin/list-pagination';
import { CabecalhoPagina, abasBentoGatilho, abasBentoLista } from '../bento/ui';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ResumoVendas } from './components/ResumoVendas';
import { useRoleDataScope } from '../commons/useRoleDataScope';
import { SalesPersonFilter } from '../commons/SalesPersonFilter';
import { FiltroVendedoresMulti } from './components/FiltroVendedoresMulti';
import { fragmentoDeVendedores, gravarSelecao, lerSelecao } from './filtroVendedores';
import { SaleList } from './components/SaleList';
import { filtroDeSegmento, type FiltroSegmento } from './segmentoVenda';
import { useSegmentoPorGrupo } from './useSegmentoPorGrupo';
import { pilulaFiltro } from './components/pecasPremium';
import { BotaoOlhinho, ValoresOcultosProvider } from '../valores-ocultos/ValoresOcultos';

type FiltroCancelamento = 'churn' | 'nao_confirmada' | 'todos';

// Abas da página de Vendas. Contemplados e Cancelados são dirigidos pela VENDA
// (contemplado=flag da base de produção; cancelados=status), não pelo lance.
type TabKey = 'completed' | 'pending' | 'contemplados' | 'cancelados';

// O olhinho envolve a tela inteira (resumo, lista e paginação): é por tela,
// e o que ficasse de fora mostraria o número de verdade.
export const VendasPage = () => (
  <ValoresOcultosProvider>
    <VendasConteudo />
  </ValoresOcultosProvider>
);

const VendasConteudo = () => {
  const navigate = useNavigate();
  // Aba ativa mora na URL (não em useState) pra sobreviver a reload — antes só
  // era LIDA da URL uma vez no mount; clicar numa aba nunca escrevia de volta,
  // então F5 sempre caía no default "Vendas Efetivadas".
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: TabKey =
    tabParam === 'pending' || tabParam === 'contemplados' || tabParam === 'cancelados' ? tabParam : 'completed';
  const initialSearch = searchParams.get('q') ?? '';
  const deepLinkReference = searchParams.get('ref') ?? '';
  // Busca e vendedor também vão para a URL (como período e segmento): ao abrir
  // uma venda e voltar, a lista monta de novo e só a URL sobrevive.
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [vendedorEscolhido, setVendedorEscolhido] = useState<string>(searchParams.get('vendedor') ?? 'all');
  const [dateFrom, setDateFrom] = useState(searchParams.get('from') ?? '');
  const [dateTo, setDateTo] = useState(searchParams.get('to') ?? '');
  // Segmento fica na URL como os demais filtros de universo, pra link
  // compartilhado e F5 caírem na mesma lista.
  const segParam = searchParams.get('seg');
  const filtroSegmento: FiltroSegmento = segParam === 'I' || segParam === 'M' ? segParam : 'todos';
  const { segmentoPorGrupo, carregado: segmentosCarregados } = useSegmentoPorGrupo();
  const { isLoading, isSuperAdmin, isManager, porUnidade, scopedCrmUserIds } = useRoleDataScope();
  // Só quem tem o seletor de vendedor filtra por ele; `vendedor` na URL de quem
  // não tem o seletor deixaria a lista vazia sem como limpar.
  const salesPersonFilter = isSuperAdmin || isManager ? vendedorEscolhido : 'all';

  // Sub-filtro da aba Cancelados. Também na URL, pelo mesmo motivo do segmento.
  //
  // A aba mostrava SÓ `churn` (cliente que pagava e parou). Fazia sentido
  // enquanto "não confirmada" era ruído de pipeline preso em `pending`; depois
  // que o sync passou a fechar essas linhas (migration 181), elas sumiam da
  // tela inteira — não estão em Pendentes porque não estão mais pendentes, e
  // não estavam aqui porque o filtro as excluía. Sumir não é resposta: agora
  // são dois recortes, e o padrão continua sendo churn.
  const kindParam = searchParams.get('cancel');
  const filtroCancelamento: FiltroCancelamento =
    kindParam === 'nao_confirmada' || kindParam === 'todos' ? kindParam : 'churn';

  const setFiltroCancelamento = (valor: FiltroCancelamento) => {
    const next = new URLSearchParams(searchParams);
    if (valor === 'churn') next.delete('cancel'); else next.set('cancel', valor);
    setSearchParams(next, { replace: true });
  };

  const setFiltroSegmento = (valor: FiltroSegmento) => {
    const next = new URLSearchParams(searchParams);
    if (valor === 'todos') next.delete('seg'); else next.set('seg', valor);
    setSearchParams(next, { replace: true });
  };

  const updateDateRange = (from: string, to: string) => {
    setDateFrom(from);
    setDateTo(to);
    const next = new URLSearchParams(searchParams);
    if (from) next.set('from', from); else next.delete('from');
    if (to) next.set('to', to); else next.delete('to');
    setSearchParams(next, { replace: true });
  };

  const atualizarBusca = (valor: string) => {
    setSearchTerm(valor);
    const next = new URLSearchParams(searchParams);
    if (valor) next.set('q', valor); else next.delete('q');
    setSearchParams(next, { replace: true });
  };

  const setSalesPersonFilter = (valor: string) => {
    setVendedorEscolhido(valor);
    const next = new URLSearchParams(searchParams);
    if (valor === 'all') next.delete('vendedor'); else next.set('vendedor', valor);
    setSearchParams(next, { replace: true });
  };

  const setActiveTab = (tab: TabKey) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  const searchQuery = searchTerm.trim();
  const saleId = searchQuery ? null : searchParams.get('sale_id');
  const contrato = searchQuery ? null : searchParams.get('contrato');
  const hasDeepLinkFilter = !!saleId || !!contrato;

  // Super admin escolhe vários (lista de ids na URL); gerente continua com um.
  const vendedoresSelecionados = useMemo(() => lerSelecao(salesPersonFilter), [salesPersonFilter]);
  const salesPersonFragment = useMemo(
    () => fragmentoDeVendedores(vendedoresSelecionados),
    [vendedoresSelecionados],
  );

  const dateRangeFragment = useMemo(() => {
    const fragment: Record<string, string> = {};
    if (dateFrom) fragment['venda_data@gte'] = dateFrom;
    if (dateTo) fragment['venda_data@lte'] = dateTo;
    return fragment;
  }, [dateFrom, dateTo]);

  // Vira `group_numbers` porque a view não tem a coluna segmento — ver
  // segmentoVenda.ts. Vale nas quatro abas: o segmento é do grupo da cota, não
  // do estado da venda.
  const segmentoFragment = useMemo(
    () => filtroDeSegmento(filtroSegmento, segmentoPorGrupo, segmentosCarregados),
    [filtroSegmento, segmentoPorGrupo, segmentosCarregados],
  );

  const completedFilter = useMemo(() => {
    const base = {
      status: 'eq.confirmed',
      // Contempladas saem daqui e vão pra aba "Contemplados".
      contemplado: 'eq.false',
      ...(saleId ? { id: saleId } : {}),
      ...(contrato ? { contrato } : {}),
      ...salesPersonFragment,
      ...dateRangeFragment,
      ...segmentoFragment,
      ...(!isSuperAdmin && !porUnidade
        ? scopedCrmUserIds.length > 0
          ? { sales_person_ids: scopedCrmUserIds }
          : { sales_person_id: -1 }
        : {}),
    };
    return searchQuery ? { ...base, q: searchQuery } : base;
  }, [saleId, contrato, searchQuery, isSuperAdmin, porUnidade, scopedCrmUserIds, salesPersonFragment, dateRangeFragment, segmentoFragment]);

  const pendingFilter = useMemo(() => {
    const base = {
      'status@in': '(started,pending,reconciled)',
      ...(saleId ? { id: saleId } : {}),
      ...salesPersonFragment,
      ...dateRangeFragment,
      ...segmentoFragment,
      ...(!isSuperAdmin && !porUnidade
        ? scopedCrmUserIds.length > 0
          ? { sales_person_ids: scopedCrmUserIds }
          : { sales_person_id: -1 }
        : {}),
      ...(contrato && { contrato }),
    };
    return searchQuery ? { ...base, q: searchQuery } : base;
  }, [isSuperAdmin, porUnidade, scopedCrmUserIds, contrato, saleId, searchQuery, salesPersonFragment, dateRangeFragment, segmentoFragment]);

  const scopeFragment = useMemo(
    () => (!isSuperAdmin && !porUnidade
      ? scopedCrmUserIds.length > 0
        ? { sales_person_ids: scopedCrmUserIds }
        : { sales_person_id: -1 }
      : {}),
    [isSuperAdmin, porUnidade, scopedCrmUserIds],
  );

  const contempladosFilter = useMemo(() => {
    const base = {
      contemplado: 'eq.true',
      ...(saleId ? { id: saleId } : {}),
      ...(contrato ? { contrato } : {}),
      ...salesPersonFragment,
      ...dateRangeFragment,
      ...segmentoFragment,
      ...scopeFragment,
    };
    return searchQuery ? { ...base, q: searchQuery } : base;
  }, [saleId, contrato, searchQuery, salesPersonFragment, dateRangeFragment, segmentoFragment, scopeFragment]);

  const canceladosFilter = useMemo(() => {
    const base = {
      status: 'eq.cancelled',
      // Padrão continua "parou de pagar" (churn): é o cancelamento que dói e o
      // que alimenta persistência. "Tentativa falhada" fica a um clique.
      ...(filtroCancelamento === 'todos' ? {} : { cancel_kind: `eq.${filtroCancelamento}` }),
      ...(saleId ? { id: saleId } : {}),
      ...(contrato ? { contrato } : {}),
      ...salesPersonFragment,
      ...dateRangeFragment,
      ...segmentoFragment,
      ...scopeFragment,
    };
    return searchQuery ? { ...base, q: searchQuery } : base;
  }, [
    saleId,
    contrato,
    searchQuery,
    salesPersonFragment,
    dateRangeFragment,
    segmentoFragment,
    scopeFragment,
    filtroCancelamento,
  ]);

  const currentView = useMemo(() => {
    switch (activeTab) {
      case 'pending':      return { filter: pendingFilter,      sort: { field: 'created_at', order: 'DESC' as const } };
      case 'contemplados': return { filter: contempladosFilter, sort: { field: 'data_contemplacao', order: 'DESC' as const } };
      case 'cancelados':   return { filter: canceladosFilter,   sort: { field: 'venda_data', order: 'DESC' as const } };
      default:             return { filter: completedFilter,    sort: { field: 'venda_data', order: 'DESC' as const } };
    }
  }, [activeTab, completedFilter, pendingFilter, contempladosFilter, canceladosFilter]);

  if (isLoading) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        Carregando...
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      {/* Resumo do ano, ACIMA das abas e fora de todo filtro: a lista abaixo
          muda com aba, busca, vendedor e período, e sem um número estável não
          dá pra saber se "caiu" é o mês fraco ou o filtro ligado. */}
      <CabecalhoPagina
        titulo="Vendas"
        aoLadoDoTitulo={<BotaoOlhinho />}
        descricao="Consórcio · confirmadas, pendentes, contempladas e canceladas"
      />
      {/* Segue período e vendedor — os filtros que mudam o universo. Não
          segue busca nem aba: busca é pra achar uma linha, e as abas são
          estados diferentes da venda. */}
      <ResumoVendas de={dateFrom} ate={dateTo} salesPersonIds={vendedoresSelecionados} />

      {/* Abas, filtros e a tabela num cartão só (Premium North). */}
      <div className="cartao bg-card">
        {/* Abas sublinhadas; a aba ativa mora na URL. */}
        <div className="sticky top-0 z-10 rounded-t-[14px] bg-card px-4 pt-3">
          <div className={cn(abasBentoLista, "flex-wrap gap-y-1 overflow-visible")}>
            {(
              [
                ['completed', 'Vendas efetivadas'],
                ['pending', 'Pagamentos pendentes'],
                ['contemplados', 'Contemplados'],
                ['cancelados', 'Cancelados'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                data-state={activeTab === key ? 'active' : 'inactive'}
                className={abasBentoGatilho}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {hasDeepLinkFilter && (
          <div className="px-4 pt-3">
            <div className="flex items-center justify-between rounded-lg bg-[#EEF1F6] px-3 py-2 text-xs font-medium text-marinho">
              <span>
                {contrato && `Filtrado pelo contrato ${contrato}`}
                {!contrato && saleId && `Filtrado por ${deepLinkReference || 'venda selecionada'}`}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchTerm('');
                  navigate(`/vendas?tab=${activeTab}`);
                }}
              >
                Limpar filtro
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-3 border-b border-border p-4">
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              placeholder="Buscar por nome ou contrato"
              value={searchTerm}
              onChange={(event) => atualizarBusca(event.target.value)}
              className="flex-1"
            />
            {/* Filtro por vendedor: super-admin (todos) ou gerente (só a equipe
                dele, escopo aplicado dentro do SalesPersonFilter). */}
            {isSuperAdmin ? (
              <FiltroVendedoresMulti
                selecionados={vendedoresSelecionados}
                aoMudar={(ids) => setSalesPersonFilter(gravarSelecao(ids))}
                className="sm:w-64"
              />
            ) : isManager && (
              <SalesPersonFilter
                value={salesPersonFilter}
                onValueChange={setSalesPersonFilter}
                className="sm:w-64"
              />
            )}
          </div>
          {activeTab === 'cancelados' && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground">Motivo:</span>
              {([
                { value: 'churn', label: 'Parou de pagar' },
                { value: 'nao_confirmada', label: 'Tentativa falhada' },
                { value: 'todos', label: 'Todos' },
              ] as const).map((opcao) => (
                <button
                  key={opcao.value}
                  type="button"
                  onClick={() => setFiltroCancelamento(opcao.value)}
                  aria-pressed={filtroCancelamento === opcao.value}
                  className={pilulaFiltro(filtroCancelamento === opcao.value)}
                >
                  {opcao.label}
                </button>
              ))}
              <span className="text-xs text-muted-foreground">
                {filtroCancelamento === 'nao_confirmada'
                  ? 'Venda não efetivada na Servopa; nenhum pagamento realizado.'
                  : filtroCancelamento === 'churn'
                    ? 'Cliente interrompeu os pagamentos.'
                    : ''}
              </span>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            {/* Segmento no mesmo botão segmentado das Comissões — é o mesmo
                recorte Imóvel/Móvel, e repetir o padrão evita o usuário ter que
                reaprender o controle de tela em tela. */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground">Segmento:</span>
              {([
                { value: 'todos', label: 'Todos' },
                { value: 'I', label: 'Imóvel' },
                { value: 'M', label: 'Móvel' },
              ] as const).map((opcao) => (
                <button
                  key={opcao.value}
                  type="button"
                  onClick={() => setFiltroSegmento(opcao.value)}
                  aria-pressed={filtroSegmento === opcao.value}
                  className={pilulaFiltro(filtroSegmento === opcao.value)}
                >
                  {opcao.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground">Período:</span>
              <Input
                type="date"
                value={dateFrom}
                onChange={(event) => updateDateRange(event.target.value, dateTo)}
                className="h-8 w-auto"
                aria-label="Data inicial"
              />
              <span className="text-xs text-muted-foreground">até</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(event) => updateDateRange(dateFrom, event.target.value)}
                className="h-8 w-auto"
                aria-label="Data final"
              />
              {(dateFrom || dateTo) && (
                <Button variant="ghost" size="sm" onClick={() => updateDateRange('', '')}>
                  Limpar período
                </Button>
              )}
            </div>
          </div>
        </div>
        <div className="pb-3 [&>div:empty]:hidden">
          <List
            resource="servopa_vendas_with_details"
            sort={currentView.sort}
            perPage={25}
            filter={currentView.filter}
            actions={false}
            pagination={<ListPagination className="px-4" />}
          >
            <SaleList segmentoPorGrupo={segmentoPorGrupo} />
            <GuardaRolagem />
          </List>
        </div>
      </div>
    </div>
  );
};

// Onde a lista estava quando saiu da tela. Quem rola é o <main> do Layout, não
// a janela, e o navegador só devolve a rolagem da janela: ao voltar de uma
// venda a lista montava de novo no topo. Vale só para a mesma URL (aba,
// filtros, página) e só na volta pelo histórico; entrar em Vendas pelo menu
// continua começando do topo.
let rolagemSalva: { busca: string; topo: number } | null = null;

const GuardaRolagem = () => {
  const { isPending } = useListContext();
  const { search } = useLocation();
  const tipoNavegacao = useNavigationType();
  const busca = useRef(search);
  busca.current = search;
  // Decidido na montagem: trocar de página dentro da lista não devolve nada.
  const alvo = useRef(
    tipoNavegacao === 'POP' && rolagemSalva?.busca === search ? rolagemSalva.topo : null,
  );

  // Layout effect: a limpeza roda antes de a lista sair do DOM, quando a
  // rolagem ainda é a da lista.
  useLayoutEffect(() => {
    const main = document.getElementById('main-content');
    if (!main) return;
    let topo = main.scrollTop;
    const aoRolar = () => { topo = main.scrollTop; };
    main.addEventListener('scroll', aoRolar, { passive: true });
    return () => {
      main.removeEventListener('scroll', aoRolar);
      rolagemSalva = { busca: busca.current, topo };
    };
  }, []);

  // Devolve depois que as linhas chegam, por alguns quadros: a altura da
  // página ainda pode crescer e o navegador corta o que passa do fim.
  useEffect(() => {
    const topo = alvo.current;
    const main = document.getElementById('main-content');
    if (isPending || topo === null || !main) return;
    alvo.current = null;
    let quadros = 0;
    let quadro = 0;
    const devolver = () => {
      main.scrollTop = topo;
      if (Math.abs(main.scrollTop - topo) > 1 && ++quadros < 30) quadro = requestAnimationFrame(devolver);
    };
    devolver();
    return () => cancelAnimationFrame(quadro);
  }, [isPending]);

  return null;
};
